import { useEffect, useRef, type RefObject } from 'react';

import type { ImperativePanelHandle } from 'react-resizable-panels';

import { applyPanelSize } from '@/shared/utils';

import { hasChosenChatWidth } from './chat-column-sizes';

export interface IChatColumnDefaultWidthProps {
  /** The chat panel's imperative handle. */
  panelRef: RefObject<ImperativePanelHandle | null>;
  /** The panel group's `autoSaveId`, which scopes the remembered width. */
  autoSaveId: string;
  /** Percentage the px default works out to on the measured frame. */
  defaultSize: number;
  /** Whether the frame has a usable width yet. */
  isFrameMeasured: boolean;
}

/**
 * Applies the chat column's px default width once the frame has been measured,
 * unless the user has dragged the column to a width of their own.
 *
 * `ResizablePanelGroup` only reads `defaultSize` on its first layout, before
 * the frame can be measured, so the default has to be applied imperatively.
 *
 * @param props - Component props. See {@link IChatColumnDefaultWidthProps}.
 * @returns Nothing — this component only runs the effect
 */
export function ChatColumnDefaultWidth({
  panelRef,
  autoSaveId,
  defaultSize,
  isFrameMeasured,
}: IChatColumnDefaultWidthProps) {
  const hasApplied = useRef(false);

  // eslint-disable-next-line no-restricted-syntax -- reason: the panel group's first layout is imperative, and a width measured after that layout cannot be expressed through `defaultSize`
  useEffect(() => {
    if (hasApplied.current || !isFrameMeasured) return;
    if (hasChosenChatWidth(autoSaveId)) {
      hasApplied.current = true;
      return;
    }
    hasApplied.current = applyPanelSize(panelRef.current, defaultSize);
  }, [autoSaveId, defaultSize, isFrameMeasured, panelRef]);

  return null;
}
